import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';

export default function CartPage() {
  const { cartItems, removeFromCart, updateQuantity, updateExtraQuantity, getCartTotal, getExtrasTotal, getShippingCost, getGrandTotal, clearCart } = useCart();

  const subtotal = getCartTotal();
  const extrasSubtotal = getExtrasTotal();
  const shipping = getShippingCost();
  const total = getGrandTotal();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12" data-testid="cart-page">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-white rounded-xl p-12 text-center">
            <ShoppingBag className="w-24 h-24 mx-auto text-gray-300 mb-6" />
            <h2 className="font-manrope font-bold text-2xl mb-4">Sepetiniz Boş</h2>
            <p className="text-gray-600 mb-8">Henüz sepetinize ürün eklemediniz.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
              data-testid="continue-shopping-button"
            >
              Alışverişe Başla
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h1 className="font-manrope font-bold text-2xl md:text-3xl mb-6">Sepetim</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl p-4 md:p-6 flex gap-4"
                data-testid={`cart-item-${item.id}`}
              >
                <Link to={`/urun/${item.id}`} className="flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg"
                  />
                </Link>

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <Link to={`/urun/${item.id}`}>
                        <h3 className="font-semibold text-gray-900 mb-2 hover:text-trendyol-orange transition-colors line-clamp-2">
                          {item.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600">Satıcı: {item.seller}</p>
                      {item.isFreeShipping && (
                        <p className="text-sm text-green-600 font-medium mt-1">Ücretsiz Kargo</p>
                      )}
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors h-fit"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border-2 border-gray-200 rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                        data-testid={`decrease-quantity-${item.id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-4 font-semibold" data-testid={`quantity-${item.id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                        data-testid={`increase-quantity-${item.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="font-manrope font-bold text-xl text-trendyol-orange">
                        {(item.price * item.quantity).toFixed(2)} TL
                      </p>
                      {item.quantity > 1 && (
                        <p className="text-sm text-gray-500">{item.price.toFixed(2)} TL / adet</p>
                      )}
                    </div>
                  </div>

                  {Array.isArray(item.extras) && item.extras.length > 0 && (
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="text-xs font-semibold text-gray-700">Ekstralar</div>
                      <div className="mt-2 space-y-1">
                        {item.extras.map((ex) => (
                          <div key={String(ex.extra_id)} className="flex items-center justify-between gap-3">
                            {ex.ui_id ? (
                              <Link
                                to={`/urun/${ex.ui_id}`}
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                className="flex items-center gap-2 min-w-0"
                              >
                                <div className="h-8 w-8 rounded-md bg-gray-200 overflow-hidden flex-shrink-0">
                                  {ex.extra_image ? (
                                    <img src={ex.extra_image} alt={ex.extra_name || ''} className="h-full w-full object-cover" />
                                  ) : null}
                                </div>
                                <div className="text-xs text-gray-700 truncate">
                                  {ex.extra_name || 'Ekstra'}
                                </div>
                              </Link>
                            ) : (
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="h-8 w-8 rounded-md bg-gray-200 overflow-hidden flex-shrink-0">
                                  {ex.extra_image ? (
                                    <img src={ex.extra_image} alt={ex.extra_name || ''} className="h-full w-full object-cover" />
                                  ) : null}
                                </div>
                                <div className="text-xs text-gray-700 truncate">
                                  {ex.extra_name || 'Ekstra'}
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateExtraQuantity(item.id, ex.extra_id, Math.max((Number(ex.quantity) || 1) - 1, 0))}
                                  className="px-2 py-1 hover:bg-gray-100 transition-colors"
                                  data-testid={`decrease-extra-${item.id}-${ex.extra_id}`}
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="px-2 text-xs font-semibold" data-testid={`extra-qty-${item.id}-${ex.extra_id}`}>
                                  {Number(ex.quantity) || 1}
                                </span>
                                <button
                                  onClick={() => updateExtraQuantity(item.id, ex.extra_id, (Number(ex.quantity) || 1) + 1)}
                                  className="px-2 py-1 hover:bg-gray-100 transition-colors"
                                  data-testid={`increase-extra-${item.id}-${ex.extra_id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="text-[11px] text-gray-500 whitespace-nowrap">
                                {(Number(ex.extra_price) || 0).toFixed(2)} TL / adet
                              </div>
                              <div className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                                {((Number(ex.extra_price) || 0) * (Number(ex.quantity) || 1)).toFixed(2)} TL
                              </div>
                              <button
                                onClick={() => updateExtraQuantity(item.id, ex.extra_id, 0)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                data-testid={`remove-extra-${item.id}-${ex.extra_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button
              onClick={clearCart}
              className="text-red-500 hover:text-red-600 font-medium text-sm"
              data-testid="clear-cart-button"
            >
              Sepeti Temizle
            </button>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 sticky top-24">
              <h3 className="font-semibold text-lg mb-4">Sipariş Özeti</h3>

              <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
                <div className="flex justify-between text-gray-600">
                  <span>Ürün Toplamı</span>
                  <span>{subtotal.toFixed(2)} TL</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Ekstra Toplamı</span>
                  <span>{extrasSubtotal.toFixed(2)} TL</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Kargo Toplamı</span>
                  <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
                    {shipping === 0 ? 'ÜCRETSIZ' : `${shipping.toFixed(2)} TL`}
                  </span>
                </div>
              </div>

              <div className="flex justify-between font-manrope font-bold text-xl mb-6">
                <span>Toplam</span>
                <span className="text-trendyol-orange">{total.toFixed(2)} TL</span>
              </div>

              {shipping > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    150 TL üzeri alışverişlerde <strong>ücretsiz kargo</strong>!
                  </p>
                  <div className="mt-2 bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min((subtotal / 150) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    {(150 - subtotal).toFixed(2)} TL daha ekleyin
                  </p>
                </div>
              )}

              <Link
                to="/odeme"
                className="w-full block text-center bg-trendyol-orange hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors mb-3"
                data-testid="checkout-button"
              >
                Alışverişi Tamamla
              </Link>

              <Link
                to="/"
                className="block text-center text-trendyol-orange hover:underline font-medium"
                data-testid="continue-shopping-link"
              >
                Alışverişe Devam Et
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}